import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import './AdminLoginPage.css';

// Fallback to original admin UUID if settings don't exist
const FALLBACK_ADMIN_UID = 'sZQ7z2w0IIf14AQAEQgKqgaqrB32';

const AdminLoginPage = () => {
  const { t } = useLanguage();
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authLoading || !isAdmin) return;
    const from = location.state?.from;
    if (typeof from === 'string' && from.startsWith('/admin') && !from.startsWith('/admin/login')) {
      navigate(from, { replace: true });
      return;
    }
    let savedTab = 'competitions';
    try {
      const saved = sessionStorage.getItem('ezkAdminTab');
      if (['competitions', 'users', 'challenges', 'rounds', 'settings'].includes(saved)) {
        savedTab = saved;
      }
    } catch (err) {
      savedTab = 'competitions';
    }
    navigate(`/admin/${savedTab}`, { replace: true });
  }, [isAdmin, authLoading, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Load admin UUIDs from Firestore
      let adminUUIDs = [FALLBACK_ADMIN_UID];
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'global'));
        if (settingsDoc.exists()) {
          const uuids = settingsDoc.data().adminUUIDs || [];
          adminUUIDs = uuids.length > 0 ? uuids : [FALLBACK_ADMIN_UID];
        }
      } catch (err) {
        console.error('Error loading admin UUIDs:', err);
      }
      
      // Check if the logged-in user is in the admin list
      if (!adminUUIDs.includes(userCredential.user.uid)) {
        await auth.signOut();
        setError(t('accessDenied'));
        setLoading(false);
        return;
      }

      // Auth context updates isAdmin, and the effect above returns to the previous tab.
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || t('invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-container">
        <h1>{t('admin')} {t('login')}</h1>
        <form onSubmit={handleSubmit} className="admin-login-form">
          <div className="form-group">
            <label htmlFor="email">{t('email')} <span className="required-indicator">*</span></label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('password')} <span className="required-indicator">*</span></label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          {error && <div className="error">{error}</div>}

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? t('loggingIn') : t('login')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLoginPage;

